import { API, deleteRequest, getRequest, patchRequest } from '@/api/api';
import { useAuth } from '@/store/auth/auth.store';
import { hasAnyRole, UserRole } from '@/utils/roles';

import type {
  TaskDocument,
  TaskParticipant,
  TaskResponse,
  TasksFilter,
  TasksListResponse,
} from './tasks.dto';

/**
 * Service tasks are somebody else's work order until you are the one who can act on
 * them, so only these roles get them mixed into a list.
 */
export const shouldIncludeServiceTasks = (): boolean => {
  const session = useAuth.getState().session;
  return hasAnyRole(session?.userRoles, [
    UserRole.SERVICE_PROVIDER,
    UserRole.CAPTAIN,
    UserRole.OWNER,
    UserRole.CO_OWNER,
  ]);
};

/** Crew aboard see this vessel's service tasks only, not every service they belong to. */
export const shouldShowOnlyCurrentServiceTask = (): boolean => {
  const session = useAuth.getState().session;
  return hasAnyRole(session?.userRoles, [
    UserRole.OWNER,
    UserRole.CO_OWNER,
    UserRole.CAPTAIN,
    UserRole.ENGINEER,
    UserRole.CREW_MEMBER,
  ]);
};

export const fetchTaskById = async (id: string): Promise<TaskResponse> => {
  const response = await getRequest<TaskResponse>(API.ACTIVITY, `/tasks/${id}`);
  return response.data;
};

/**
 * `TasksFilter` as activity's `GET /tasks` query string.
 *
 * The date fields are the one place the two vocabularies differ: activity takes a single
 * `from`/`to` pair over the task's time slot, so `starts_from` and `ends_to` map onto it.
 * `starts_to` and `ends_from` have no counterpart on the service and are dropped rather
 * than sent as parameters it would ignore.
 */
export const buildTasksFilterQuery = (filter: TasksFilter): string => {
  const params = new URLSearchParams();
  params.append('limit', filter.limit.toString());
  params.append('page', filter.page.toString());

  if (filter.type) params.append('type', filter.type);
  if (filter.status && filter.status.length > 0) {
    filter.status.forEach((status) => params.append('status', status));
  }
  if (filter.priority) params.append('priority', filter.priority);
  if (filter.from) params.append('from', filter.from);
  if (filter.to) params.append('to', filter.to);
  if (filter.starts_from) params.append('from', filter.starts_from);
  if (filter.ends_to) params.append('to', filter.ends_to);
  if (filter.due_from) params.append('due_from', filter.due_from);
  if (filter.due_to) params.append('due_to', filter.due_to);
  if (filter.supervisor) params.append('supervisor', filter.supervisor);
  if (filter.responsible) params.append('responsible', filter.responsible);
  if (filter.equipment_id) params.append('equipment_id', filter.equipment_id);
  if (filter.participant_status && filter.participant_status.length > 0) {
    filter.participant_status.forEach((status) => params.append('participant_status', status));
  }
  if (filter.sort) {
    params.append('sort', filter.sort);
    if (filter.order) params.append('order', filter.order);
  }

  if (shouldIncludeServiceTasks()) {
    params.append('include_service_tasks', 'true');
  }

  if (shouldShowOnlyCurrentServiceTask()) {
    params.append('only_current_service_tasks', 'true');
  }

  return params.toString();
};

/**
 * The tasks list, as the service answers it — no store, no cache, no shared state.
 *
 * Every caller that renders a *filtered* list must go through this (or the react-query
 * hooks over it) rather than through the `tasksList` store slice: that slice is one
 * list shared by the calendar, the dashboard widgets and the task pickers, so whichever
 * fetch lands last wins and a page's own filter silently becomes somebody else's.
 */
export const fetchTasks = async (filter: TasksFilter): Promise<TasksListResponse> => {
  const query = buildTasksFilterQuery({
    ...filter,
    participant_status: filter.participant_status ?? ['unknown', 'accepted', 'maybe'],
  });
  const response = await getRequest<TasksListResponse>(API.ACTIVITY, `/tasks?${query}`);
  return response.data;
};

export const getTasksByResponsible = async (responsibleId: string): Promise<TaskResponse[]> => {
  const response = await getRequest<{ count: number; items: TaskResponse[] }>(
    API.ACTIVITY,
    '/tasks',
    undefined,
    {
      page: 1,
      limit: 100,
      responsible: responsibleId,
    },
  );
  return response.data.items;
};

export const addTaskParticipants = async (id: string, participants: TaskParticipant[]) => {
  await patchRequest(API.ACTIVITY, `/tasks/${id}/participants`, {
    participants,
  });
};

export const deleteTaskParticipants = async (id: string, participantIds: string[]) => {
  await deleteRequest(API.ACTIVITY, `/tasks/${id}/participants`, {
    participants: participantIds,
  });
};

export const getTasksByEventId = async (eventId: string): Promise<TaskResponse[]> => {
  const response = await getRequest<{ count: number; items: TaskResponse[] }>(
    API.ACTIVITY,
    '/tasks',
    undefined,
    {
      page: 1,
      limit: 100,
      event_id: eventId,
    },
  );
  return response.data.items;
};

/**
 * Appends documents to a task — activity keeps up to `TASK_DOCUMENTS_MAX` of them
 * and answers with the files it stored. Every file goes under the same `files`
 * key, which is how the multipart form carries a list; `request` switches to
 * multipart on its own for FormData.
 */
export const attachTaskDocuments = async (id: string, files: File[]): Promise<TaskDocument[]> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const response = await patchRequest<TaskDocument[], FormData>(
    API.ACTIVITY,
    `/tasks/${id}/document`,
    formData,
  );
  return response.data ?? [];
};

/** Detaches one document, leaving the task's other files alone. */
export const deleteTaskDocument = async (id: string, documentId: string) => {
  await deleteRequest(API.ACTIVITY, `/tasks/${id}/document/${documentId}`);
};
